import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "../../lib/utils"
import { useOverlayOwner, useScopedOverlayState } from "./overlay-scope"

const tooltipSurfaceStyles = "max-w-[calc(100vw-2rem)] bg-foreground font-sans text-background shadow-[0_16px_40px_-18px_rgb(0_0_0/0.65)]"
const richTooltipStyles = "w-56 rounded-2xl px-4 py-3.5 text-[13px] leading-4"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const state = useScopedOverlayState(props)
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} {...state} />
    </TooltipProvider>
  )
}

type TooltipTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean
}

function TooltipTrigger({
  onFocus,
  onClick,
  onKeyDown,
  onPointerDown,
  onPointerMove,
  ...props
}: TooltipTriggerProps) {
  const focusSuppression = React.useRef<{
    ownerDocument: Document
    onKeyDown: (event: KeyboardEvent) => void
  } | null>(null)
  const resumeFocusTooltip = React.useCallback(() => {
    const suppression = focusSuppression.current
    if (!suppression) return
    suppression.ownerDocument.removeEventListener("keydown", suppression.onKeyDown, true)
    focusSuppression.current = null
  }, [])
  const suppressRestoredFocus = (trigger: HTMLButtonElement) => {
    const ownerDocument = trigger.ownerDocument
    if (focusSuppression.current?.ownerDocument === ownerDocument) return
    resumeFocusTooltip()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return
      const popup = (event.target as Element | null)?.closest?.('[role="menu"], [role="dialog"], [role="listbox"]')
      // Tab inside the activated popup is not a new visit to its trigger.
      if (!popup || popup.contains(trigger)) resumeFocusTooltip()
    }
    focusSuppression.current = { ownerDocument, onKeyDown }
    ownerDocument.addEventListener("keydown", onKeyDown, true)
  }
  React.useEffect(() => resumeFocusTooltip, [resumeFocusTooltip])

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...(props as React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>)}
      onPointerDown={event => {
        suppressRestoredFocus(event.currentTarget)
        onPointerDown?.(event)
      }}
      onClick={event => {
        suppressRestoredFocus(event.currentTarget)
        onClick?.(event)
      }}
      onKeyDown={event => {
        if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
          suppressRestoredFocus(event.currentTarget)
        }
        onKeyDown?.(event)
      }}
      onFocus={event => {
        onFocus?.(event)
        // Dismissing a popup restores focus, without starting a new tooltip visit.
        if (focusSuppression.current || event.currentTarget.getAttribute("aria-expanded") === "true") {
          event.preventDefault()
        }
      }}
      onPointerMove={event => {
        onPointerMove?.(event)
        if (event.pointerType === "touch" || event.defaultPrevented) return
        if (event.currentTarget.getAttribute("aria-expanded") === "true") {
          event.preventDefault()
        } else {
          resumeFocusTooltip()
        }
      }}
    />
  )
}

type TooltipContentProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean
  variant?: "compact" | "rich"
  side?: "top" | "right" | "bottom" | "left"
  sideOffset?: number
  align?: "start" | "center" | "end"
  alignOffset?: number
  avoidCollisions?: boolean
  collisionPadding?: number | Partial<Record<"top" | "right" | "bottom" | "left", number>>
  sticky?: "partial" | "always"
  hideWhenDetached?: boolean
}

function TooltipContent({
  className,
  variant = "compact",
  side = "top",
  sideOffset = 8,
  children,
  ...props
}: TooltipContentProps) {
  const owner = useOverlayOwner()
  if (owner && !owner.active) return null
  const arrow = variant === "compact"
    ? side === "left" || side === "right"
      ? { width: 10, height: 7, viewBox: "0 0 10 7", path: "M0 0h10L5.8 5.6Q5 6.6 4.2 5.6Z" }
      : { width: 28, height: 9, viewBox: "0 0 28 9", path: "M0 0c5 0 7 1 9 3l4 4c1 1 2 1 3 0l4-4c2-2 4-3 8-3Z" }
    : { width: 60, height: 16, viewBox: "0 0 60 16", path: "M0 0c10 0 14 1 19 5l8 7c2 2 4 2 6 0l8-7c5-4 9-5 19-5Z" }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) text-balance",
          tooltipSurfaceStyles,
          variant === "compact"
            ? "rounded-xl px-3 py-2 text-xs"
            : richTooltipStyles,
          className
        )}
        {...(props as React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>)}
        data-overlay-owner={owner?.id}
      >
        {children}
        <TooltipPrimitive.Arrow asChild width={arrow.width} height={arrow.height}>
          <svg
            viewBox={arrow.viewBox}
            className="z-50 -translate-y-px fill-foreground"
            aria-hidden="true"
          >
            <path d={arrow.path} />
          </svg>
        </TooltipPrimitive.Arrow>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

function TooltipSurface({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="tooltip-surface"
      className={cn(tooltipSurfaceStyles, richTooltipStyles, "w-max min-w-52", className)}
      {...props}
    />
  )
}

function TooltipTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      data-slot="tooltip-title"
      className={cn("text-[13px] font-semibold leading-4 tracking-[-0.005em]", className)}
      {...props}
    />
  )
}

function TooltipDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      data-slot="tooltip-description"
      className={cn("mt-1.5 text-xs font-medium leading-[1.45] text-background/70", className)}
      {...props}
    />
  )
}

function TooltipMetricList({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="tooltip-metric-list" className={cn("grid gap-2.5", className)} {...props} />
}

function TooltipMetricRow({
  indicator,
  indicatorClassName,
  label,
  value,
  labelClassName,
  valueClassName,
  className,
}: {
  indicator?: React.ReactNode
  indicatorClassName?: string
  label: React.ReactNode
  value: React.ReactNode
  labelClassName?: string
  valueClassName?: string
  className?: string
}) {
  const hasIndicator = indicator !== undefined

  return (
    <div
      data-slot="tooltip-metric-row"
      className={cn(
        "grid min-h-6 items-center gap-x-2.5",
        hasIndicator
          ? "grid-cols-[1.5rem_minmax(0,1fr)_auto]"
          : "grid-cols-[minmax(0,1fr)_auto]",
        className
      )}
    >
      {hasIndicator ? (
        <span
          className={cn(
            "grid size-6 place-items-center rounded-full bg-background/10 [&>svg]:size-3 [&>svg_*]:[stroke-width:2]",
            indicatorClassName
          )}
        >
          {indicator}
        </span>
      ) : null}
      <span className={cn("min-w-0 text-[13px] font-medium leading-4 tracking-[-0.005em] text-background/70", labelClassName)}>
        {label}
      </span>
      <span className={cn("justify-self-end text-sm font-semibold leading-4 tracking-[-0.01em] tabular-nums", valueClassName)}>
        {value}
      </span>
    </div>
  )
}

function TooltipDivider({ className }: { className?: string }) {
  return (
    <svg
      data-slot="tooltip-divider"
      className={cn("h-0.5 w-full text-background/20", className)}
      aria-hidden="true"
    >
      <line
        x1="1"
        y1="1"
        x2="100%"
        y2="1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="6 7"
      />
    </svg>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipSurface,
  TooltipTitle,
  TooltipDescription,
  TooltipMetricList,
  TooltipMetricRow,
  TooltipDivider,
}
